import type { TranslationKey } from "@veycue/i18n";
import { useNavigate } from "react-router-dom";
import type { Cue, HealthResponse } from "../api/types";
import { CueList } from "../components/CueList";
import { EmptyState, ErrorState, Loading, Page } from "../components/states";
import { useApi } from "../hooks/useApi";
import { useSession } from "../state/session";

/**
 * Overview odpowiada na pytanie „co dzieje się w mojej firmie" (§4B) — inaczej niż
 * mobilne Today, które odpowiada „co mam teraz zrobić". Stąd metryki na górze
 * i pełniejszy obraz zamiast dwóch kart.
 */
export function Overview() {
  const { organization, translator } = useSession();
  const { t } = translator;
  const navigate = useNavigate();

  const base = organization ? `/v1/organizations/${organization.id}` : null;
  const health = useApi<HealthResponse>(base ? `${base}/health` : null);
  const cues = useApi<{ cues: Cue[] }>(base ? `${base}/cues?status=new,viewed&limit=5` : null);

  if (!organization) {
    return (
      <Page title={t("nav.overview")}>
        <EmptyState
          title={t("onboarding.nip.title")}
          description={t("onboarding.nip.description")}
        />
      </Page>
    );
  }

  const openCues = cues.data?.cues ?? [];
  const critical = openCues.filter((cue) => cue.severity === "critical").length;
  const score = health.data?.score ?? null;
  const scoreLabel =
    score === null ? "" : score >= 80 ? t("health.good") : score >= 50 ? t("health.needsAttention") : t("health.poor");

  return (
    <Page title={organization.name} lead={t("common.tagline")}>
      {health.error !== null && <ErrorState error={health.error} />}

      <div className="cards">
        <section className="card">
          <p className="card__label">{t("health.title")}</p>
          {health.loading ? (
            <Loading />
          ) : (
            <>
              <p className="metric">
                {score ?? "—"}
                <span className="metric__suffix"> / 100</span>
              </p>
              <p className="muted">{scoreLabel}</p>

              {/* Wynik bez uzasadnienia byłby marketingowym numerem — §24 tego zabrania. */}
              {health.data && health.data.breakdown.length > 0 && (
                <ul className="breakdown">
                  {health.data.breakdown.map((factor) => (
                    <li key={factor.key}>
                      <span>{t(`health.factor.${factor.key}` as TranslationKey)}</span>
                      <b>−{factor.points}</b>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <section className="card">
          <p className="card__label">{t("nav.cues")}</p>
          <p className="metric">{cues.loading ? "—" : openCues.length}</p>
          <p className="muted">
            {critical > 0 ? t("cue.severity.critical") : t("today.allClear")}
          </p>
        </section>

        <section className="card">
          <p className="card__label">{t("nav.contracts")}</p>
          <p className="metric">0</p>
          <p className="muted">{t("documents.empty")}</p>
        </section>
      </div>

      <h2 className="page__title" style={{ fontSize: "var(--vc-text-h2)" }}>
        {t("nav.attentionCenter")}
      </h2>

      {cues.error !== null && <ErrorState error={cues.error} />}
      {cues.loading ? (
        <Loading />
      ) : openCues.length === 0 ? (
        <EmptyState title={t("today.nothingToDo")} description={t("today.allClear")} />
      ) : (
        <CueList cues={openCues} onSelect={(cue) => navigate(`/cues?highlight=${cue.id}`)} />
      )}
    </Page>
  );
}
